import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import qs from 'qs';

import Table from '../../components/table';
import { parseLuxonDateToParts } from '../../lib/date';
import { ProcessedFlightHistory, Status } from '../../types/flight';
import { statusToColorMap } from '../viewFlightContainer/ColorStatus';
import { toTitleCase } from '../../lib/strings';
import { iataOrFallBackToLabeledFS } from '../viewFlightContainer/FlightViewBody';

interface FlightSearchResultsTableProps {
  data: ProcessedFlightHistory[];
  filterCarrierIata?: string;
  filterFlightNum?: string;
}

interface ColoredStatusTextProps {
  status?: Status;
}

const ColoredStatusText = styled.div<ColoredStatusTextProps>`
  color: ${props =>
    statusToColorMap[toTitleCase(props.status ?? '')]?.body || '#000000'};
  font-weight: 700;
`;

const emptyFieldCharacter = '--';

const dataToTableData = ({
  data,
  filterCarrierIata,
  filterFlightNum,
}: FlightSearchResultsTableProps) => {
  return data?.map(flight => {
    // find the carrier that matches the filtered one, if any
    const codeshareFlight = flight.codeshares?.find(
      codeshare =>
        // if the filter carrier matches a codeshare and the flightNumber filter is either empty or matching this codeshare, prefer this codeshare
        (codeshare.carrierDetails?.iata === filterCarrierIata &&
          (!filterFlightNum || codeshare.flightNumber === filterFlightNum)) ||
        // OR if a flight number does not match the operated by, but DOES match a codeshare, regardless of whether they searched a specific carrier
        (flight.carrierDetails?.iata !== filterFlightNum &&
          codeshare.flightNumber === filterFlightNum),
    );
    const marketedCarrierIata =
      codeshareFlight?.carrierDetails?.iata || flight.carrierDetails?.iata;
    const marketedCarrierFs =
      codeshareFlight?.carrierDetails?.fs || flight.carrierDetails?.fs;
    const flightNumber =
      codeshareFlight?.flightNumber ||
      flight.flightNumber ||
      emptyFieldCharacter;

    const carrierDisplayName = iataOrFallBackToLabeledFS({
      iata: marketedCarrierIata,
      fs: marketedCarrierFs,
    });
    const flightDisplayName = `${carrierDisplayName} ${flightNumber}`;

    const departureDateTime = flight.publishedDeparture ||
      flight.scheduledGateDeparture ||
      flight.actualGateDeparture ||
      flight.estimatedGateDeparture ||
      flight.scheduledRunwayDeparture ||
      flight.actualRunwayDeparture ||
      flight.estimatedRunwayDeparture;

    const arrivalDateTime = flight.publishedArrival ||
      flight.scheduledGateArrival ||
      flight.actualGateArrival ||
      flight.estimatedGateArrival ||
      flight.scheduledRunwayArrival ||
      flight.actualRunwayArrival ||
      flight.estimatedRunwayArrival;

    const parsedDepartureTime = departureDateTime ? parseLuxonDateToParts(departureDateTime) : null;
    const parsedArrivalTime = arrivalDateTime ? parseLuxonDateToParts(arrivalDateTime) : null;

    const linkQuery = qs.stringify({
      carrier: marketedCarrierIata,
      flightNumber,
    });

    return {
      key: `row${flight.id}`,
      values: [
        <Link
          key={`tableLink${flight.id}`}
          to={`/flights/${flight.id}?${linkQuery}`}
        >
          {flightDisplayName || emptyFieldCharacter}
        </Link>,
        <>
          {`${parsedDepartureTime?.time12Hr} ${parsedDepartureTime?.dayPeriod} ${parsedDepartureTime?.timeZoneShortName}`}
          <br />
          {parsedDepartureTime?.displayDateFormat}
        </>,
        <>
          {`${parsedArrivalTime?.time12Hr} ${parsedArrivalTime?.dayPeriod} ${parsedArrivalTime?.timeZoneShortName}`}
          <br />
          {parsedArrivalTime?.displayDateFormat}
        </>,
        // departureAirport
        iataOrFallBackToLabeledFS(flight.departureDetails),
        // arrivalAirport
        iataOrFallBackToLabeledFS(flight.arrivalDetails),
        <ColoredStatusText key={`Status${flight.id}`} status={flight.status}>
          {flight.status}
        </ColoredStatusText>,
      ],
    };
  });
};

const FlightSearchResultsTable: React.FC<FlightSearchResultsTableProps> = ({
  data,
  filterCarrierIata,
  filterFlightNum,
}) => {
  const headers = [
    { header: 'Flight' },
    { header: 'Departure Time' },
    { header: 'Arrival Time' },
    { header: 'Departure Airport' },
    { header: 'Arrival Airport' },
    { header: 'Status' },
  ];
  const noRowsValue = 'No Flight Records Match Filters';
  return (
    <>
      <Table
        headers={headers}
        data={dataToTableData({
          data,
          filterCarrierIata: filterCarrierIata,
          filterFlightNum: filterFlightNum,
        })}
        noRowsValue={noRowsValue}
      />
    </>
  );
};

export default FlightSearchResultsTable;
